import React, { useState, useEffect, useCallback } from "react";
import {
    TextField,
    Typography,
    FormControl,
    Box,
    Chip,
    CircularProgress
} from "@mui/material";
import ShowSinglePatternTable from "./ShowSinglePatternTable";
import { getSearchOneShoe } from "../services/api";

const SearchShoe = () => {
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [inputValue, setInputValue] = useState(""); // To hold current input value
    const [shoeNumbers, setShoeNumbers] = useState([]);

    const handleSearch = useCallback(() => {
        if (shoeNumbers.length === 0) return;

        setLoading(true);
        getSearchOneShoe(shoeNumbers)
            .then((response) => setSearchResults(response))
            .catch((error) =>
                console.error("Error occurred while sending data:", error)
            )
            .finally(() => setLoading(false));
    }, [shoeNumbers]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const addShoeNumber = () => {
        const newNumbers = inputValue
            .split(/[,\n]/) // Split on comma (`,`) or Enter (`\n`)
            .map((num) => num.trim()) // Remove spaces
            .filter((num) => num !== "" && !isNaN(num) && parseInt(num, 10) > 0); // Validate numbers

        if (newNumbers.length > 0) {
            setShoeNumbers((prevShoeNumbers) => {
                // Avoid duplicates
                const updatedShoeNumbers = [...new Set([...prevShoeNumbers, ...newNumbers])];
                return updatedShoeNumbers;
            });
            setInputValue(""); // Clear input
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault(); // Prevent form submission or unwanted behavior
            addShoeNumber();
        }
    };

    const handleDelete = (numToDelete) => {
        setShoeNumbers(shoeNumbers.filter((num) => num !== numToDelete));
    };

    // Trigger the search whenever shoeNumbers is updated
    useEffect(() => {
        handleSearch();
    }, [handleSearch]); // Dependency on shoeNumbers

    return (
        <div>
            <Typography variant="h5" gutterBottom align="center">
                Input a Shoe number [1 - 25000]:
            </Typography>

            <Box display="flex" justifyContent="center" gap={2} mb={2}>
                <Box display="flex" flexDirection="column" gap={1} width={450}>
                    <FormControl>
                        <TextField
                            label="Enter numbers, separated by commas or press Enter"
                            placeholder="Enter numbers, separated by commas or press Enter"
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyPress} // Handle comma and Enter key
                        />
                    </FormControl>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {shoeNumbers.map((num, index) => (
                            <Chip
                                key={index}
                                label={num}
                                onDelete={() => handleDelete(num)}
                                sx={{
                                    backgroundColor: "#fff", // White background
                                    color: "#000", // Black text for better contrast
                                    border: "1px solid #ddd", // Light border for better visibility
                                    "& .MuiChip-deleteIcon": {
                                        color: "#ccc", // grey delete (X) button
                                        "&:hover": {
                                            color: "darkred", // Dark red on hover
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress />
                </Box>
            )}

            {searchResults &&
                Object.entries(searchResults.dataAll).map(([key, value], index) => (
                    <ShowSinglePatternTable key={index} data={value} shoeDataAll={searchResults.shoeDataAll[key]} />
                ))}
        </div>
    );
};

export default SearchShoe;
